import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ConversationList from './ConversationList';
import DataGrid from './DataGrid';
import KnowledgeBase from './KnowledgeBase';
import ConfirmDialog from './ConfirmDialog';
import { api } from '../lib/api';
import { useLocale } from '../lib/LocaleContext';
import { CustomerStage } from '../lib/types';
import styles from './AdminDashboard.module.css';

const TAB_CONVERSATIONS = 'conversations';
const TAB_CUSTOMERS = 'customers';
const TAB_KNOWLEDGE = 'knowledge';

export default function AdminDashboard() {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const industry = searchParams.get('industry') || 'ev';
  const [activeTab, setActiveTab] = useState(TAB_KNOWLEDGE);
  const [conversations, setConversations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedStage, setSelectedStage] = useState('');
  const [updatingStage, setUpdatingStage] = useState(null);
  const [deletingCustomerId, setDeletingCustomerId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingDeleteCustomerId, setPendingDeleteCustomerId] = useState(null);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === TAB_KNOWLEDGE || tab === TAB_CONVERSATIONS || tab === TAB_CUSTOMERS) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const fetchConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const params = { limit: 100 };
      if (industry) params.industry = industry;
      const result = await api.conversations(params);
      setConversations(result.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingConversations(false);
    }
  }, [industry]);

  const fetchCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const params = { limit: 100 };
      if (selectedStage) params.stage = selectedStage;
      if (industry) params.industry = industry;
      const result = await api.customers(params);
      setCustomers(result.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingCustomers(false);
    }
  }, [selectedStage, industry]);

  useEffect(() => {
    fetchConversations();
    fetchCustomers();
  }, [fetchConversations, fetchCustomers]);

  const handleStageChange = async (customerId, newStage) => {
    if (updatingStage === customerId) return;
    setUpdatingStage(customerId);
    try {
      const result = await api.patchCustomer(customerId, newStage);
      if (result.success || result.data) {
        setCustomers(prev => prev.map(c => c.customer_id === customerId ? { ...c, stage: newStage } : c));
      } else {
        alert(`${t.updateFailed}${result.error || t.unknownError}`);
      }
    } catch (e) {
      alert(t.updateStageError);
    } finally {
      setUpdatingStage(null);
    }
  };

  const handleDeleteCustomer = (customerId, e) => {
    e.stopPropagation();
    setPendingDeleteCustomerId(customerId);
    setShowConfirmDialog(true);
  };

  const confirmDeleteCustomer = async () => {
    if (!pendingDeleteCustomerId) return;
    const cid = pendingDeleteCustomerId;
    setShowConfirmDialog(false);
    setPendingDeleteCustomerId(null);
    setDeletingCustomerId(cid);
    try {
      const result = await api.deleteCustomer(cid);
      if (result.success) {
        await fetchCustomers();
        await fetchConversations();
      } else {
        alert(`${t.deleteFailed}${result.error || t.unknownError}`);
      }
    } catch (e) {
      alert(t.deleteCustomerError);
    } finally {
      setDeletingCustomerId(null);
    }
  };

  const customerColumns = [
    { key: 'customer_id', label: t.customerId, width: '200px', render: v => <span className={styles.monoCell}>{v || '-'}</span> },
    { key: 'name', label: t.name, width: 120, render: v => <span>{v || '-'}</span> },
    { key: 'phone_number', label: t.phone, width: 120, render: v => <span>{v || '-'}</span> },
    { key: 'email', label: t.email, width: 180, render: v => <span className={styles.emailCell}>{v || '-'}</span> },
    {
      key: 'stage',
      label: t.stage,
      width: 150,
      render: (v, row) => (
        <select
          value={v || 'Inquiry'}
          onChange={e => handleStageChange(row.customer_id, e.target.value)}
          disabled={updatingStage === row.customer_id}
          className={styles.stageSelect}
        >
          {CustomerStage.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      )
    },
    { key: 'created_at', label: t.createdAt, width: 180 },
    {
      key: 'actions',
      label: t.actions,
      width: 90,
      render: (_, row) => (
        <button
          className={styles.deleteButton}
          onClick={e => handleDeleteCustomer(row.customer_id, e)}
          disabled={deletingCustomerId === row.customer_id}
          title={t.deleteCustomer}
        >
          {deletingCustomerId === row.customer_id ? t.deleting : t.delete}
        </button>
      )
    }
  ];

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <div className={styles.leftHeader}>
          <h1>管理后台</h1>
          <p>知识库 · 会话历史 · 客户记录</p>
        </div>
        <Link className={styles.backBtn} to="/">返回对话主页</Link>
      </header>

      <div className={styles.tabBar}>
        <button className={`${styles.tabBtn} ${activeTab === TAB_KNOWLEDGE ? styles.active : ''}`} onClick={() => setActiveTab(TAB_KNOWLEDGE)}>知识库</button>
        <button className={`${styles.tabBtn} ${activeTab === TAB_CONVERSATIONS ? styles.active : ''}`} onClick={() => setActiveTab(TAB_CONVERSATIONS)}>会话历史</button>
        <button className={`${styles.tabBtn} ${activeTab === TAB_CUSTOMERS ? styles.active : ''}`} onClick={() => setActiveTab(TAB_CUSTOMERS)}>客户记录</button>
      </div>

      <section className={styles.content}>
        {activeTab === TAB_CONVERSATIONS && (
          <ConversationList
            title={t.conversationList}
            data={conversations}
            loading={loadingConversations}
            onRefresh={fetchConversations}
          />
        )}

        {activeTab === TAB_CUSTOMERS && (
          <DataGrid
            title={t.customerList}
            columns={customerColumns}
            data={customers}
            loading={loadingCustomers}
            onRefresh={fetchCustomers}
            headerActions={
              <div className={styles.stageFilter}>
                <label className={styles.stageFilterLabel}>{t.filterByStage}</label>
                <select value={selectedStage} onChange={e => setSelectedStage(e.target.value)} className={styles.stageSelect}>
                  <option value="">{t.allStages}</option>
                  {CustomerStage.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            }
          />
        )}

        {activeTab === TAB_KNOWLEDGE && (
          <KnowledgeBase embedded industryCode={industry || 'ev'} />
        )}
      </section>

      <ConfirmDialog
        show={showConfirmDialog && !!pendingDeleteCustomerId}
        title={t.confirmDelete}
        confirmText={t.delete}
        cancelText={t.cancel}
        confirmVariant="danger"
        loading={!!deletingCustomerId}
        onConfirm={confirmDeleteCustomer}
        onCancel={() => {
          setShowConfirmDialog(false);
          setPendingDeleteCustomerId(null);
        }}
      >
        <p>{t.confirmDeleteCustomerMessage} <strong>{pendingDeleteCustomerId?.substring(0, 20)}</strong>{t.confirmDeleteCustomerSuffix}</p>
        <p>{t.confirmDeleteCustomerNote}</p>
      </ConfirmDialog>
    </main>
  );
}
